import React, { useRef, useState } from "react";
import { Download, Flame, Upload } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CHUNK = 5 * 1024 * 1024;
const rid = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
const fmt = (v) => Math.round(v).toLocaleString("it-IT");

export default function EaGasDailyImport({ company, askConfirm }) {
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState(null);
  const ref = useRef(null);
  const template = async () => {
    try {
      const r = await api.get("/ea/gas/daily/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      Object.assign(document.createElement("a"), { href: url, download: "Template_gas_giornaliero_EA.xlsx" }).click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Template non disponibile"); }
  };
  const send = async (files) => {
    const all = [];
    for (const f of files) {
      const id = rid(), total = Math.max(1, Math.ceil(f.size / CHUNK));
      try {
        let last;
        for (let i = 0; i < total; i++) {
          setBusy(`${f.name} · ${i < total - 1 ? `${Math.round(((i + 1) / total) * 100)}%` : "elaborazione…"}`);
          const fd = new FormData();
          Object.entries({ upload_id: id, index: String(i), total: String(total), filename: f.name, company_id: company.id }).forEach(([k, v]) => fd.append(k, v));
          fd.append("file", f.slice(i * CHUNK, (i + 1) * CHUNK), f.name);
          last = (await api.post("/ea/gas/daily/import/chunk", fd, { timeout: 300000 })).data;
        }
        all.push(...last.report);
      } catch (err) { all.push({ file: f.name, error: err.response?.data?.detail || "import non riuscito" }); }
    }
    setReport(all);
    setBusy(null);
    toast[all.some((r) => r.error) ? "warning" : "success"]("Import consumi gas completato");
  };
  const pick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (!company) return toast.error("Seleziona prima il cliente in alto");
    askConfirm(`${files.length} file di consumi giornalieri gas`, () => send(files));
  };
  return (
    <div className="card-elevated p-5" data-testid="ea-gas-daily-import">
      <div className="flex items-center gap-2 font-display font-semibold text-slate-900"><Flame size={18} className="text-blue-600" /> Import consumi giornalieri Gas (Verbali SNAM e template)</div>
      <p className="text-sm text-slate-500 mt-1 max-w-3xl">Carica i Verbali di Misura SNAM in PDF (uno o più, anche in un file ZIP) per i clienti diretti con REMI, oppure il template Excel per REMI/PDR. Il codice REMI del verbale (anche il vecchio codice "ex") deve corrispondere a un punto Gas del cliente. Lo stesso giorno reimportato sostituisce il precedente.</p>
      <div className="flex flex-wrap gap-3 mt-4">
        <button onClick={template} className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm inline-flex items-center gap-2" data-testid="ea-gas-template"><Download size={14} /> Scarica template</button>
        <button onClick={() => ref.current?.click()} disabled={!!busy || !company} className="h-9 px-4 rounded-lg bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="ea-gas-upload"><Upload size={15} /> {busy || "Importa PDF / ZIP / Excel"}</button>
        <input ref={ref} type="file" multiple accept=".pdf,.zip,.xlsx,.xlsm" className="hidden" onChange={pick} data-testid="ea-gas-upload-input" />
      </div>
      {report && (
        <div className="mt-4 space-y-1.5 text-sm" data-testid="ea-gas-import-report">
          {report.map((r, i) => (
            <div key={i} className={`rounded-lg border px-3 py-2 ${r.error ? "border-red-200 bg-red-50 text-red-700" : "border-slate-200"}`}>
              <span className="font-semibold">{r.file}</span>{r.error ? ` · ${r.error}` : ` · REMI/PDR ${r.point} · ${r.days} giorni dal ${r.from.split("-").reverse().join("/")} al ${r.to.split("-").reverse().join("/")} · ${fmt(r.smc)} Smc`}
              {(r.errors || []).map((e) => <div key={e} className="text-xs text-amber-700">{e}</div>)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
